import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  GridItem,
  Heading,
  Link,
  List,
  ListItem,
  SimpleGrid,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import {
  categorizeDepartments,
  DepartmentCategory,
} from '../common/libs/department';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import monitoring from '../common/libs/monitoring';
import {
  isDoctorRecruiterAccount as isDoctorRecruiterAccountFunc,
  isPharmaceuticalAccount as isPharmaceuticalAccountFunc,
} from '../common/libs/user';
import { useAppContext } from '../contexts/app';
import useApiClient from '../hooks/useApiClient';

type GetDepartmentsRes = {
  departments: {
    id: number;
    name: string;
    articlesCount: number;
    displayOrder: number;
    category: DepartmentCategory;
  }[];
};
type DepartmentsByCategory = {
  [key in DepartmentCategory]: GetDepartmentsRes['departments'];
};
type SpecialArticle = {
  articleId: string;
  displayOrder: number;
  name: string | null;
  description: string | null;
  topImage: string | null;
  externalUrl: string | null;
  isPrivate: boolean;
};
type GetSpecialArticlesRes = {
  specialArticles: SpecialArticle[];
};

type GlobalNavAreaPresentationProps = {
  departmentsMap: DepartmentsByCategory;
  specialArticles: SpecialArticle[];
  userId: string | number | null;
  isPharmaceuticalAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  onClickLink?: (category: string, id: string) => void;
};
const GlobalNavAreaPresentation: FC<GlobalNavAreaPresentationProps> = (
  props,
) => {
  const {
    departmentsMap,
    specialArticles,
    userId,
    isPharmaceuticalAccount,
    isDoctorRecruiterAccount,
    onClickLink,
  } = props;

  return (
    <nav>
      <SimpleGrid
        templateColumns={{ base: '1fr', md: '2fr 1fr' }}
        columnGap={4}
        marginBottom={4}
      >
        <SimpleGrid
          columns={{ base: 2, md: 3 }}
          alignContent="start"
          columnGap={10}
        >
          <GridItem colSpan={{ base: 2, md: 3 }}>
            <Heading
              as="h3"
              fontSize="0.75rem"
              fontWeight={600}
              lineHeight={1.5}
              color="textSecondary"
            >
              診療科
            </Heading>
          </GridItem>
          <Box as="section">
            <Box as="section" paddingLeft="0.5em">
              <Heading
                as="h4"
                fontSize="0.75rem"
                fontWeight={600}
                lineHeight={1.5}
                color="textSecondary"
                marginY="4px"
              >
                内科
              </Heading>
              <List
                paddingLeft="0.5em"
                fontSize="0.75rem"
                fontWeight={400}
                lineHeight={1.5}
              >
                {departmentsMap['内科'].map((department) => {
                  return (
                    <ListItem key={department.id}>
                      <Link
                        href={`/department/${department.id}`}
                        color="textSecondary"
                        onClick={() => {
                          onClickLink?.(
                            'globalnav_department',
                            String(department.id),
                          );
                        }}
                        display="inline-block"
                        paddingY={{ base: '4px', md: 0 }}
                      >
                        {department.name}
                        <ChevronRightIcon color="primaryDark" />
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
          <Box as="section" paddingLeft="0.5em">
            <Heading
              as="h4"
              fontSize="0.75rem"
              fontWeight={600}
              lineHeight={1.5}
              color="textSecondary"
              marginY="4px"
            >
              外科
            </Heading>
            <List
              paddingLeft="0.5em"
              fontSize="0.75rem"
              fontWeight={400}
              lineHeight={1.5}
            >
              {departmentsMap['外科'].map((department) => {
                return (
                  <ListItem key={department.id}>
                    <Link
                      href={`/department/${department.id}`}
                      color="textSecondary"
                      onClick={() => {
                        onClickLink?.(
                          'globalnav_department',
                          String(department.id),
                        );
                      }}
                      display="inline-block"
                      paddingY={{ base: '4px', md: 0 }}
                    >
                      {department.name}
                      <ChevronRightIcon color="primaryDark" />
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Box
            as="section"
            paddingLeft="0.5em"
            marginTop={{ base: '20px', md: 0 }}
          >
            <Heading
              as="h4"
              fontSize="0.75rem"
              fontWeight={600}
              lineHeight={1.5}
              color="textSecondary"
              marginY="4px"
            >
              その他
            </Heading>
            <List
              paddingLeft="0.5em"
              fontSize="0.75rem"
              fontWeight={400}
              lineHeight={1.5}
            >
              {departmentsMap['その他'].map((department) => {
                return (
                  <ListItem key={department.id}>
                    <Link
                      href={`/department/${department.id}`}
                      color="textSecondary"
                      onClick={() => {
                        onClickLink?.(
                          'globalnav_department',
                          String(department.id),
                        );
                      }}
                      display="inline-block"
                      paddingY={{ base: '4px', md: 0 }}
                    >
                      {department.name}
                      <ChevronRightIcon color="primaryDark" />
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          <GridItem
            colSpan={{ base: 1, md: 3 }}
            paddingLeft={{ base: '0.5em', md: 0 }}
            marginTop={{ base: '20px', md: 0 }}
          >
            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              alignContent="start"
              columnGap={10}
              rowGap={{ base: '20px', md: 0 }}
            >
              <Box as="section" marginTop={{ base: 0, md: '1rem' }}>
                <Heading
                  as="h3"
                  fontSize="0.75rem"
                  fontWeight={600}
                  lineHeight={1.5}
                  color="textSecondary"
                  marginY="4px"
                >
                  その他
                </Heading>
                <List
                  paddingLeft="0.5em"
                  fontSize="0.75rem"
                  fontWeight={400}
                  lineHeight={1.5}
                >
                  <ListItem>
                    <Link
                      href="/articles"
                      color="textSecondary"
                      onClick={() => {
                        onClickLink?.('globalnav_others', '/articles');
                      }}
                      display="inline-block"
                      paddingY={{ base: '4px', md: 0 }}
                    >
                      全てのスライド
                      <ChevronRightIcon color="primaryDark" />
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      href="/weekly-ranking-articles"
                      color="textSecondary"
                      onClick={() => {
                        onClickLink?.(
                          'globalnav_others',
                          '/weekly-ranking-articles',
                        );
                      }}
                      display="inline-block"
                      paddingY={{ base: '4px', md: 0 }}
                    >
                      週間スライドランキング
                      <ChevronRightIcon color="primaryDark" />
                    </Link>
                  </ListItem>
                  {userId && (
                    <ListItem>
                      <Link
                        href={`/profile/${userId}`}
                        color="textSecondary"
                        onClick={() => {
                          onClickLink?.(
                            'globalnav_others',
                            `/profile/${userId}`,
                          );
                        }}
                        display="inline-block"
                        paddingY={{ base: '4px', md: 0 }}
                      >
                        プロフィール
                        <ChevronRightIcon color="primaryDark" />
                      </Link>
                    </ListItem>
                  )}
                  {userId && (
                    <ListItem>
                      <Link
                        href="/mypage/view-history"
                        color="textSecondary"
                        onClick={() => {
                          onClickLink?.(
                            'globalnav_others',
                            '/mypage/view-history',
                          );
                        }}
                        display="inline-block"
                        paddingY={{ base: '4px', md: 0 }}
                      >
                        閲覧したスライド
                        <ChevronRightIcon color="primaryDark" />
                      </Link>
                    </ListItem>
                  )}
                  {!isPharmaceuticalAccount && userId && (
                    <ListItem>
                      <Link
                        href="/mypage"
                        color="textSecondary"
                        onClick={() => {
                          onClickLink?.('globalnav_others', '/mypage');
                        }}
                        display="inline-block"
                        paddingY={{ base: '4px', md: 0 }}
                      >
                        投稿したスライド
                        <ChevronRightIcon color="primaryDark" />
                      </Link>
                    </ListItem>
                  )}
                </List>
              </Box>
              {!isPharmaceuticalAccount &&
                !isDoctorRecruiterAccount &&
                userId && (
                  <Box as="section" marginTop={{ base: 0, md: '1rem' }}>
                    <Heading
                      as="h3"
                      fontSize="0.75rem"
                      fontWeight={600}
                      lineHeight={1.5}
                      color="textSecondary"
                      marginY="4px"
                    >
                      保存
                    </Heading>
                    <List
                      paddingLeft="0.5em"
                      fontSize="0.75rem"
                      fontWeight={400}
                      lineHeight={1.5}
                    >
                      <ListItem>
                        <Link
                          href="/mypage/like/articles"
                          color="textSecondary"
                          onClick={() => {
                            onClickLink?.(
                              'globalnav_others',
                              '/mypage/like/articles',
                            );
                          }}
                          display="inline-block"
                          paddingY={{ base: '4px', md: 0 }}
                        >
                          保存スライド
                          <ChevronRightIcon color="primaryDark" />
                        </Link>
                      </ListItem>
                      <ListItem>
                        <Link
                          href="/mypage/like/pages"
                          color="textSecondary"
                          onClick={() => {
                            onClickLink?.(
                              'globalnav_others',
                              '/mypage/like/pages',
                            );
                          }}
                          display="inline-block"
                          paddingY={{ base: '4px', md: 0 }}
                        >
                          保存ページ
                          <ChevronRightIcon color="primaryDark" />
                        </Link>
                      </ListItem>
                    </List>
                  </Box>
                )}
              <Box as="section" marginTop={{ base: 0, md: '1rem' }}>
                <Heading
                  as="h3"
                  fontSize="0.75rem"
                  fontWeight={600}
                  lineHeight={1.5}
                  color="textSecondary"
                  marginY="4px"
                >
                  ガイド
                </Heading>
                <List
                  paddingLeft="0.5em"
                  fontSize="0.75rem"
                  fontWeight={400}
                  lineHeight={1.5}
                >
                  <ListItem>
                    <Link
                      href="/docs/academy"
                      color="textSecondary"
                      onClick={() => {
                        onClickLink?.('globalnav_others', '/docs/academy');
                      }}
                      display="inline-block"
                      paddingY={{ base: '4px', md: 0 }}
                    >
                      スライド作成完全ガイド
                      <ChevronRightIcon color="primaryDark" />
                    </Link>
                  </ListItem>
                  <ListItem>
                    <Link
                      href="/docs/shosekika"
                      color="textSecondary"
                      onClick={() => {
                        onClickLink?.('globalnav_others', '/docs/shosekika');
                      }}
                      display="inline-block"
                      paddingY={{ base: '4px', md: 0 }}
                    >
                      書籍化までの道のり
                      <ChevronRightIcon color="primaryDark" />
                    </Link>
                  </ListItem>
                </List>
              </Box>
            </SimpleGrid>
          </GridItem>
        </SimpleGrid>

        {specialArticles.length > 0 && (
          <Box
            as="section"
            borderLeft={{ base: 'none', md: '1px solid #D8D8D8' }}
            borderTop={{ base: '1px solid #D8D8D8', md: 'none' }}
            paddingLeft={{ base: 0, md: '1rem' }}
            paddingTop={{ base: '1rem', md: 0 }}
            marginTop={{ base: '1rem', md: 0 }}
          >
            <Heading
              as="h3"
              fontSize="0.75rem"
              fontWeight={600}
              lineHeight={1.5}
              color="textSecondary"
            >
              特集
            </Heading>
            <Box as="section" paddingLeft="0.5em">
              <Heading
                as="h4"
                fontSize="0.75rem"
                fontWeight={400}
                lineHeight={1.5}
                color="textSecondary"
                marginY="4px"
              >
                <Link
                  href="/special"
                  color="textSecondary"
                  onClick={() => {
                    onClickLink?.('globalnav_special', '/special');
                  }}
                  display="inline-block"
                  paddingY={{ base: '4px', md: 0 }}
                >
                  特集TOP
                  <ChevronRightIcon color="primaryDark" />
                </Link>
              </Heading>
              <List
                paddingLeft="0.5em"
                fontSize="0.75rem"
                fontWeight={400}
                lineHeight={1.5}
              >
                {specialArticles.map((article) => {
                  return (
                    <ListItem key={article.articleId}>
                      <Link
                        href={`/special/${article.articleId}`}
                        color="textSecondary"
                        onClick={() => {
                          onClickLink?.('globalnav_special', article.articleId);
                        }}
                        display="inline-block"
                        paddingY={{ base: '4px', md: 0 }}
                      >
                        {article.name}
                        <ChevronRightIcon color="primaryDark" />
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
            <Box as="section" marginTop={'1rem'}>
              <Heading
                as="h3"
                fontSize="0.75rem"
                fontWeight={600}
                lineHeight={1.5}
                color="textSecondary"
              >
                イベント
              </Heading>
              <List
                paddingLeft="0.5em"
                fontSize="0.75rem"
                fontWeight={400}
                lineHeight={1.5}
              >
                <ListItem>
                  <Link
                    href="/events/new-residents-orientation"
                    color="textSecondary"
                    onClick={() => {
                      onClickLink?.(
                        'globalnav_events',
                        '/events/new-residents-orientation',
                      );
                    }}
                    display="inline-block"
                    paddingY={{ base: '4px', md: 0 }}
                    marginRight={-3}
                  >
                    （2026年度）新・研修医向け1dayオリエンテーション
                    <ChevronRightIcon color="primaryDark" />
                  </Link>
                </ListItem>
              </List>
            </Box>
          </Box>
        )}
      </SimpleGrid>
    </nav>
  );
};

export const GlobalNavArea: FC = () => {
  const { apiClient } = useApiClient();
  const { user } = useAppContext();

  const [departmentsMap, setDepartmentsMap] = useState<DepartmentsByCategory>({
    内科: [],
    外科: [],
    その他: [],
  });
  const [specialArticles, setSpecialArticles] = useState<SpecialArticle[]>([]);

  // 診療科一覧取得
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient('/api/departments?withArticleCount=true', {
          method: 'GET',
        });
        if (res.ok) {
          const data = (await res.json()) as GetDepartmentsRes;
          setDepartmentsMap(
            categorizeDepartments(data.departments) as DepartmentsByCategory,
          );
        } else {
          throw new Error(res.statusText);
        }
      } catch (e) {
        monitoring.notify({
          name: `API ERROR DETECTED ${e}`,
          message: '診療科の取得に失敗しました。',
        });
      }
    })();
  }, [apiClient]);

  // 特集一覧取得
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient('/api/article/special-articles', {
          method: 'GET',
        });
        if (res.ok) {
          const data = (await res.json()) as GetSpecialArticlesRes;
          // 外部リンクの特集は除外する
          setSpecialArticles(
            data.specialArticles.filter((article) => !article.externalUrl),
          );
        } else {
          throw new Error(res.statusText);
        }
      } catch (e) {
        monitoring.notify({
          name: `API ERROR DETECTED ${e}`,
          message: '特集の取得に失敗しました。',
        });
      }
    })();
  }, [apiClient]);

  // リンククリックのGAイベント送信
  const sendClickLinkEvent = (category: string, id: string) => {
    sendAnalyticsEvent(category, 'click', id);
  };

  return (
    <GlobalNavAreaPresentation
      departmentsMap={departmentsMap}
      specialArticles={specialArticles}
      userId={user?.discloseId ?? user?.id ?? null}
      isPharmaceuticalAccount={isPharmaceuticalAccountFunc(user)}
      isDoctorRecruiterAccount={isDoctorRecruiterAccountFunc(user)}
      onClickLink={sendClickLinkEvent}
    />
  );
};
